import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
} from "react";
import axios from "axios";
import { useLocation } from "react-router-dom";

const PermissionsContext = createContext();

export function PermissionsProvider({ children }) {
  const [permissions, setPermissions] = useState([]);
  const [permissionsMapping, setPermissionsMapping] = useState({});
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem("accessToken"));
  const [loginUserID, setLoginUserID] = useState(
    JSON.parse(localStorage.getItem("admin"))?.data?.id || null
  );
  const location = useLocation();

  const fetchUserDetails = async (userId) => {
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API}get-user-details-byid`,
        {
          user_id: JSON.parse(localStorage.getItem("admin"))?.data?.id || null,
        },
        {
          headers: {
            "x-access-token": localStorage.getItem("accessToken"),
            "Content-Type": "application/json",
          },
        }
      );
      return response?.data?.data?.roleId;
    } catch (error) {
      console.error("Error fetching user details:", error);
      return null;
    }
  };

  const fetchPermissionsData = async (roleId) => {
    try {
      const [permissionsResponse, mappingResponse] = await Promise.all([
        axios.get(
          `${process.env.REACT_APP_API}admin/roles/${roleId}/permissions`,
          {
            headers: { "x-access-token": token },
          }
        ),
        axios.get(`${process.env.REACT_APP_API}admin/permissions-mapping`, {
          headers: { "x-access-token": token },
        }),
      ]);

      setPermissions(permissionsResponse.data.permissions || []);
      setPermissionsMapping(mappingResponse.data.permissions || {});
    } catch (error) {
      console.error("Error fetching permissions data:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      if (token && loginUserID) {
        setLoading(true);
        const roleId = await fetchUserDetails(loginUserID);
        if (roleId) {
          await fetchPermissionsData(roleId);
        } else {
          setLoading(false);
        }
      } else {
        clearPermissions();
        setLoading(false);
      }
    };

    fetchData();
  }, [token, loginUserID]);

  const syncAuthState = useCallback(() => {
    const currentToken = localStorage.getItem("accessToken");
    const userData = JSON.parse(localStorage.getItem("admin"));
    const userID = userData?.data?.id || null;
    if (currentToken !== token || userID !== loginUserID) {
      setToken(currentToken);
      setLoginUserID(userID);
    }
  }, [token, loginUserID]);

  useEffect(() => {
    syncAuthState();
  }, [location, syncAuthState]);

  useEffect(() => {
    window.addEventListener("storage", syncAuthState);
    window.addEventListener("focus", syncAuthState);
    return () => {
      window.removeEventListener("storage", syncAuthState);
      window.removeEventListener("focus", syncAuthState);
    };
  }, [syncAuthState]);

  const clearPermissions = () => {
    setPermissions([]);
    setPermissionsMapping({});
  };

  return (
    <PermissionsContext.Provider
      value={{ permissions, permissionsMapping, loading, clearPermissions }}
    >
      {children}
    </PermissionsContext.Provider>
  );
}

export function usePermissions() {
  return useContext(PermissionsContext);
}
